import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Boxes,
  Download,
  FileSpreadsheet,
  History,
  Plus,
  Save,
  Search,
  Upload,
  X,
} from "lucide-react";
import { downloadSpreadsheetTemplate, parseSpreadsheetFile } from "@/utils/spreadsheet";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const createEmptyOpeningRow = () => ({
  name: "",
  item_code: "",
  description: "",
  hsn_sac: "",
  unit: "NOS",
  gst_rate: "",
  opening_qty: "",
  rate: "",
});

const OPENING_TEMPLATE_ROWS = [
  {
    "Item Name": "HP Laptop",
    "Item Code": "HP-LAP-001",
    Description: "15 inch laptop",
    "HSN/SAC": "84713010",
    Unit: "NOS",
    "GST Rate": 18,
    "Opening Qty": 5,
    Rate: 45000,
  },
];

const normalizeText = (value) => String(value || "").trim();

const normalizeUploadedOpeningRow = (row = {}) => ({
  name: normalizeText(row.name || row.item_name || row["Item Name"] || row["Item name"]),
  item_code: normalizeText(row.item_code || row.itemCode || row["Item Code"] || row.Code),
  description: normalizeText(row.description || row.Description),
  hsn_sac: normalizeText(row.hsn_sac || row.hsn || row.sac || row["HSN/SAC"]),
  unit: normalizeText(row.unit || row.Unit) || "NOS",
  gst_rate: normalizeText(row.gst_rate || row["GST Rate"] || row.gstRate),
  opening_qty: normalizeText(
    row.opening_qty || row.openingQty || row.opening_quantity || row["Opening Qty"] || row.Quantity,
  ),
  rate: normalizeText(row.rate || row.Rate),
});

const getProcessedMessage = (label, data) => {
  const processedCount = (data?.created_count || 0) + (data?.updated_count || 0);
  const skippedCount = data?.skipped?.length || 0;
  return `${label} ${processedCount} row(s)${skippedCount ? ` and skipped ${skippedCount} row(s)` : ""}.`;
};

const getHistoryTypeLabel = (entry) => {
  if (entry.source === "opening_stock") {
    return "Opening Stock";
  }
  return entry.invoice_type === "purchase" ? "Purchase" : "Sales";
};

export default function Inventory() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [openingRows, setOpeningRows] = useState([createEmptyOpeningRow()]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewRows, setPreviewRows] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemHistory, setItemHistory] = useState([]);

  const loadPage = async (searchValue = "") => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const [summaryResponse, itemsResponse] = await Promise.all([
        axios.get(`${API}/inventory/summary`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/inventory/items`, {
          params: searchValue ? { search: searchValue } : {},
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setSummary(summaryResponse.data || null);
      setItems(itemsResponse.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load inventory");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadPage(searchTerm);
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const updateOpeningRow = (index, field, value) => {
    setOpeningRows((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row,
      ),
    );
  };

  const updatePreviewRow = (index, field, value) => {
    setPreviewRows((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row,
      ),
    );
  };

  const addOpeningRow = () => {
    setOpeningRows((prev) => [...prev, createEmptyOpeningRow()]);
  };

  const removeOpeningRow = (index) => {
    setOpeningRows((prev) =>
      prev.length === 1
        ? [createEmptyOpeningRow()]
        : prev.filter((_, rowIndex) => rowIndex !== index),
    );
  };

  const removePreviewRow = (index) => {
    setPreviewRows((prev) => prev.filter((_, rowIndex) => rowIndex !== index));
  };

  const saveOpeningStockRows = async (entries, onSuccess) => {
    const validEntries = entries
      .map((entry) => ({
        ...entry,
        name: normalizeText(entry.name),
        item_code: normalizeText(entry.item_code),
        description: normalizeText(entry.description),
        hsn_sac: normalizeText(entry.hsn_sac),
        unit: normalizeText(entry.unit) || "NOS",
        gst_rate: normalizeText(entry.gst_rate),
        opening_qty: normalizeText(entry.opening_qty),
        rate: normalizeText(entry.rate),
      }))
      .filter((entry) => entry.name && entry.opening_qty);

    if (validEntries.length === 0) {
      toast.error("Please add at least one row with item name and opening quantity");
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/inventory/opening-stock/bulk`,
        { entries: validEntries },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(getProcessedMessage("Processed", response.data));
      if (onSuccess) {
        onSuccess();
      }
      loadPage(searchTerm);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save opening stock");
    } finally {
      setSaving(false);
    }
  };

  const handleManualSave = async () => {
    await saveOpeningStockRows(openingRows, () => {
      setOpeningRows([createEmptyOpeningRow()]);
    });
  };

  const handlePreviewSave = async () => {
    await saveOpeningStockRows(previewRows, () => {
      setPreviewRows([]);
      setPreviewOpen(false);
    });
  };

  const handleTemplateDownload = () => {
    downloadSpreadsheetTemplate("inventory-opening-qty-format.xlsx", OPENING_TEMPLATE_ROWS, "Opening Stock");
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setImporting(true);
      const sheetRows = await parseSpreadsheetFile(file);
      const normalizedRows = sheetRows.map(normalizeUploadedOpeningRow);

      if (!normalizedRows.some((row) => row.name && row.opening_qty)) {
        toast.error("The uploaded file does not contain any opening stock rows");
        return;
      }

      setPreviewRows(normalizedRows);
      setPreviewOpen(true);
    } catch (error) {
      toast.error("Failed to read the uploaded Excel file");
    } finally {
      event.target.value = "";
      setImporting(false);
    }
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const openHistory = async (item) => {
    try {
      setSelectedItem(item);
      setHistoryOpen(true);
      setHistoryLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/inventory/ledger`, {
        params: { normalized_name: item.normalized_name },
        headers: { Authorization: `Bearer ${token}` },
      });
      setItemHistory(response.data || []);
    } catch (error) {
      toast.error("Failed to load item history");
      setItemHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-bold bg-gradient-to-r from-primary-600 to-orange-600 bg-clip-text text-transparent">
              Inventory
            </h1>
            <p className="text-xs text-gray-500 mt-0.5">
              Add or upload opening stock for multiple items, preview the rows, edit them, and then save them in one go.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" className="text-xs" onClick={addOpeningRow}>
              <Plus size={14} className="mr-1.5" />
              Add Row
            </Button>
            <Button type="button" variant="outline" className="text-xs" onClick={handleTemplateDownload}>
              <Download size={14} className="mr-1.5" />
              Download Format
            </Button>
            <label className="inline-flex">
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                onChange={handleFileUpload}
              />
              <span className="inline-flex items-center rounded-md bg-white border border-gray-300 px-3 py-2 text-xs cursor-pointer hover:bg-gray-50">
                <Upload size={14} className="mr-1.5" />
                {importing ? "Reading File..." : "Excel Upload"}
              </span>
            </label>
          </div>
        </div>

        <div className="grid md:grid-cols-4 gap-3">
          <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
            <CardContent className="p-4">
              <div className="text-xs text-gray-500">Total Items</div>
              <div className="text-xl font-bold text-gray-900 mt-1">
                {summary?.total_items || 0}
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
            <CardContent className="p-4">
              <div className="text-xs text-gray-500">Current Stock Qty</div>
              <div className="text-xl font-bold text-gray-900 mt-1">
                {Number(summary?.total_stock_qty || 0).toFixed(2)}
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
            <CardContent className="p-4">
              <div className="text-xs text-gray-500">Out of Stock</div>
              <div className="text-xl font-bold text-gray-900 mt-1">
                {summary?.out_of_stock || 0}
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
            <CardContent className="p-4">
              <div className="text-xs text-gray-500">Item Rows</div>
              <div className="text-xl font-bold text-gray-900 mt-1">
                {items.length}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
          <CardHeader>
            <CardTitle className="text-sm font-bold text-gray-800">
              Opening Stock Entry
            </CardTitle>
            <CardDescription className="text-xs text-gray-500">
              Required Excel columns: `Item Name`, `Item Code`, `Description`, `HSN/SAC`, `Unit`, `GST Rate`, `Opening Qty`, `Rate`.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {openingRows.map((row, index) => (
              <div key={index} className="grid lg:grid-cols-8 gap-2 items-end">
                <div className="space-y-1">
                  <Label className="text-xs">Item Name</Label>
                  <Input
                    value={row.name}
                    onChange={(e) => updateOpeningRow(index, "name", e.target.value)}
                    className="h-9 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Item Code</Label>
                  <Input
                    value={row.item_code}
                    onChange={(e) => updateOpeningRow(index, "item_code", e.target.value)}
                    className="h-9 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Description</Label>
                  <Input
                    value={row.description}
                    onChange={(e) => updateOpeningRow(index, "description", e.target.value)}
                    className="h-9 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">HSN/SAC</Label>
                  <Input
                    value={row.hsn_sac}
                    onChange={(e) => updateOpeningRow(index, "hsn_sac", e.target.value)}
                    className="h-9 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Unit</Label>
                  <Input
                    value={row.unit}
                    onChange={(e) => updateOpeningRow(index, "unit", e.target.value.toUpperCase())}
                    className="h-9 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">GST Rate</Label>
                  <Input
                    value={row.gst_rate}
                    onChange={(e) => updateOpeningRow(index, "gst_rate", e.target.value)}
                    className="h-9 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Opening Qty</Label>
                  <Input
                    value={row.opening_qty}
                    onChange={(e) => updateOpeningRow(index, "opening_qty", e.target.value)}
                    className="h-9 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Rate</Label>
                  <div className="flex gap-2">
                    <Input
                      value={row.rate}
                      onChange={(e) => updateOpeningRow(index, "rate", e.target.value)}
                      className="h-9 text-xs"
                    />
                    <Button type="button" variant="outline" className="h-9 text-xs" onClick={() => removeOpeningRow(index)}>
                      Remove
                    </Button>
                  </div>
                </div>
              </div>
            ))}

            <div className="flex justify-end">
              <Button
                type="button"
                onClick={handleManualSave}
                disabled={saving}
                className="bg-gradient-to-r from-primary-500 to-orange-500 hover:from-primary-600 hover:to-orange-600 text-white text-xs"
              >
                <Save size={14} className="mr-1.5" />
                {saving ? "Saving..." : "Save Opening Qty"}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
          <CardHeader>
            <CardTitle className="text-sm font-bold text-gray-800">
              Item-wise Inventory
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative max-w-md mb-4 group">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-hover:text-primary-500 transition-colors"
              />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by item name, code, or HSN/SAC"
                className="pl-9 pr-9 h-9 text-sm"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            {loading ? (
              <div className="py-12 flex justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead>Code / HSN</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead>Purchase Qty</TableHead>
                      <TableHead>Sales Qty</TableHead>
                      <TableHead>Current Stock</TableHead>
                      <TableHead>Last Invoice Date</TableHead>
                      <TableHead className="text-right">History</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.length > 0 ? (
                      items.map((item) => (
                        <TableRow key={item.id || item.normalized_name}>
                          <TableCell>
                            <div className="font-medium text-sm text-gray-900">
                              {item.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              {item.description || "-"}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm text-gray-800">
                              {item.item_code || "-"}
                            </div>
                            <div className="text-xs text-gray-500">
                              {item.hsn_sac || "-"}
                            </div>
                          </TableCell>
                          <TableCell>{item.unit || "-"}</TableCell>
                          <TableCell>
                            {Number(item.total_purchase_qty || 0).toFixed(2)}
                          </TableCell>
                          <TableCell>
                            {Number(item.total_sales_qty || 0).toFixed(2)}
                          </TableCell>
                          <TableCell className="font-semibold">
                            {Number(item.current_stock_qty || 0).toFixed(2)}
                          </TableCell>
                          <TableCell>{item.last_invoice_date || "-"}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-8 text-xs"
                              onClick={() => openHistory(item)}
                            >
                              <History size={12} className="mr-1.5" />
                              History
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={8}
                          className="text-center text-sm text-gray-500 py-10"
                        >
                          <div className="flex flex-col items-center gap-2">
                            <Boxes size={22} className="text-gray-300" />
                            No inventory items found yet.
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="max-w-7xl">
            <DialogHeader>
              <DialogTitle className="text-base font-bold text-gray-900">
                Review Uploaded Opening Stock
              </DialogTitle>
              <DialogDescription className="text-xs">
                Check the uploaded rows, edit values if needed, then save the opening quantity for all selected items.
              </DialogDescription>
            </DialogHeader>

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item Name</TableHead>
                    <TableHead>Item Code</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>HSN/SAC</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead>GST Rate</TableHead>
                    <TableHead>Opening Qty</TableHead>
                    <TableHead>Rate</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {previewRows.length > 0 ? (
                    previewRows.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Input
                            value={row.name}
                            onChange={(e) => updatePreviewRow(index, "name", e.target.value)}
                            className="h-9 text-xs min-w-[180px]"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={row.item_code}
                            onChange={(e) => updatePreviewRow(index, "item_code", e.target.value)}
                            className="h-9 text-xs min-w-[140px]"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={row.description}
                            onChange={(e) => updatePreviewRow(index, "description", e.target.value)}
                            className="h-9 text-xs min-w-[180px]"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={row.hsn_sac}
                            onChange={(e) => updatePreviewRow(index, "hsn_sac", e.target.value)}
                            className="h-9 text-xs min-w-[120px]"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={row.unit}
                            onChange={(e) => updatePreviewRow(index, "unit", e.target.value.toUpperCase())}
                            className="h-9 text-xs min-w-[90px]"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={row.gst_rate}
                            onChange={(e) => updatePreviewRow(index, "gst_rate", e.target.value)}
                            className="h-9 text-xs min-w-[90px]"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={row.opening_qty}
                            onChange={(e) => updatePreviewRow(index, "opening_qty", e.target.value)}
                            className="h-9 text-xs min-w-[110px]"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={row.rate}
                            onChange={(e) => updatePreviewRow(index, "rate", e.target.value)}
                            className="h-9 text-xs min-w-[110px]"
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            type="button"
                            variant="outline"
                            className="h-9 text-xs"
                            onClick={() => removePreviewRow(index)}
                          >
                            Remove
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-sm text-gray-500 py-8">
                        No uploaded rows to review.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPreviewOpen(false)}>
                Close
              </Button>
              <Button
                type="button"
                onClick={handlePreviewSave}
                disabled={saving || previewRows.length === 0}
                className="bg-gradient-to-r from-primary-500 to-orange-500 text-white"
              >
                <FileSpreadsheet size={14} className="mr-1.5" />
                {saving ? "Saving..." : "Save Uploaded Rows"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
          <DialogContent className="max-w-5xl">
            <DialogHeader>
              <DialogTitle className="text-base font-bold text-gray-900">
                Item History
              </DialogTitle>
              <DialogDescription className="text-xs">
                {selectedItem
                  ? `Purchase, sales, and opening stock history for ${selectedItem.name}`
                  : "Inventory history"}
              </DialogDescription>
            </DialogHeader>

            {historyLoading ? (
              <div className="py-12 flex justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date & Time</TableHead>
                      <TableHead>Invoice Date</TableHead>
                      <TableHead>Invoice No</TableHead>
                      <TableHead>Supplier / Dealer</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Qty</TableHead>
                      <TableHead>Rate</TableHead>
                      <TableHead>Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {itemHistory.length > 0 ? (
                      itemHistory.map((entry) => (
                        <TableRow key={entry.id}>
                          <TableCell>{formatDateTime(entry.created_at)}</TableCell>
                          <TableCell>{entry.invoice_date || "-"}</TableCell>
                          <TableCell>{entry.invoice_no || "-"}</TableCell>
                          <TableCell>{entry.party_name || "-"}</TableCell>
                          <TableCell>{getHistoryTypeLabel(entry)}</TableCell>
                          <TableCell>
                            {Number(entry.quantity || 0).toFixed(2)}
                          </TableCell>
                          <TableCell>{Number(entry.rate || 0).toFixed(2)}</TableCell>
                          <TableCell>
                            {Number(entry.total_value || 0).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={8}
                          className="text-center text-sm text-gray-500 py-10"
                        >
                          No inventory history found for this item.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
